import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BarChart3, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

export default function PollCard({ poll: initial, index = 0 }) {
  const [poll, setPoll] = useState(initial);
  const [voted, setVoted] = useState(() => localStorage.getItem(`asl_vote_${initial.id}`));
  const [busy, setBusy] = useState(false);
  const total = poll.options.reduce((s, o) => s + o.votes, 0);

  const vote = async (optId) => {
    if (voted || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/polls/${poll.id}/vote`, { option_id: optId });
      setPoll(data);
      localStorage.setItem(`asl_vote_${poll.id}`, optId);
      setVoted(optId);
      toast.success("Vote counted!");
    } catch {
      toast.error("Couldn't register your vote right now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      data-testid={`poll-card-${poll.id}`}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="bg-white border border-black/5 rounded-xl p-6 sm:p-8 shadow-[0_10px_30px_-12px_rgba(0,74,173,0.15)] hover:-translate-y-1 hover:shadow-[0_20px_40px_-12px_rgba(0,74,173,0.25)] transition-[transform,box-shadow] duration-300"
    >
      <div className="flex items-center gap-2 text-league-blue mb-3">
        <BarChart3 size={16} />
        <span className="text-[11px] font-bold uppercase tracking-[0.2em]">Fan Poll</span>
      </div>
      <h3 className="font-oswald text-lg md:text-xl font-semibold text-league-ink leading-snug mb-5">{poll.question}</h3>
      <div className="space-y-2.5">
        {poll.options.map((o) => {
          const pct = total ? Math.round((o.votes / total) * 100) : 0;
          const mine = voted === o.id;
          return voted ? (
            <div key={o.id} data-testid={`poll-result-${o.id}`} className={`relative overflow-hidden rounded-lg border ${mine ? "border-league-blue" : "border-black/5"} px-4 py-3`}>
              <motion.div
                className={`absolute inset-y-0 left-0 ${mine ? "bg-league-blue/15" : "bg-league-mist"}`}
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              />
              <div className="relative flex items-center justify-between">
                <span className="text-sm font-semibold text-league-ink flex items-center gap-2">
                  {o.text}
                  {mine && <CheckCircle2 size={14} className="text-league-blue" />}
                </span>
                <span className="font-oswald text-sm font-semibold text-league-blue">{pct}%</span>
              </div>
            </div>
          ) : (
            <button
              key={o.id}
              data-testid={`vote-option-${o.id}`}
              onClick={() => vote(o.id)}
              disabled={busy}
              className="w-full text-left px-4 py-3 rounded-lg border border-black/10 text-sm font-semibold text-league-ink transition-colors duration-200 hover:border-league-blue hover:bg-league-blue hover:text-white disabled:opacity-60"
            >
              {o.text}
            </button>
          );
        })}
      </div>
      <p className="mt-4 text-xs text-muted-foreground font-medium tracking-wide" data-testid={`poll-total-${poll.id}`}>
        {total.toLocaleString()} votes {voted ? "— thanks for voting" : "— tap an option to vote"}
      </p>
    </motion.div>
  );
}
